import { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { loginSchema, type LoginInput } from '@parecer/shared';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cognitoLogin, mapCognitoErrorMessage, type AuthTokens } from '@/lib/cognito';
import { useAuthStore, userFromIdToken } from '@/store/auth';
import { useToast } from '@/components/ui/use-toast';

// Schema for the "set a new password" challenge form. Mirrors the Cognito
// pool's password policy (min 8, upper/lower/digit) so we error before the
// API call instead of relying on an `InvalidPasswordException` round-trip.
const newPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'Mínimo 8 caracteres')
      .regex(/[A-Z]/, 'Inclua uma letra maiúscula')
      .regex(/[a-z]/, 'Inclua uma letra minúscula')
      .regex(/\d/, 'Inclua um número'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  });
type NewPasswordInput = z.infer<typeof newPasswordSchema>;

export function Login() {
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState<{
    email: string;
    complete: (newPassword: string) => Promise<AuthTokens>;
  } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);
  const { toast } = useToast();

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const newPasswordForm = useForm<NewPasswordInput>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  function finishLogin(tokens: AuthTokens, email: string): void {
    const user = userFromIdToken(tokens.idToken, email);
    if (!user) throw new Error('Token de autenticação inválido — não foi possível decodificar o sub.');
    setSession(tokens, user);
    const fromState = (location.state as { from?: string } | null)?.from;
    const fromQuery = searchParams.get('from');
    const from = fromState || (fromQuery ? decodeURIComponent(fromQuery) : null);
    navigate(from ?? (user.role === 'lawyers' ? '/lawyer' : user.role === 'admins' ? '/admin' : '/app'));
  }

  async function onSubmit(values: LoginInput) {
    setLoading(true);
    try {
      const result = await cognitoLogin(values.email, values.password);
      if (result.kind === 'newPasswordRequired') {
        // Cognito demanded a permanent password (admin-created user or temp
        // password). Swap the form inline — no navigation, keep the email
        // we already have.
        setChallenge({ email: values.email, complete: result.complete });
        toast({
          variant: 'warning',
          title: 'Defina sua senha definitiva',
          description: 'Sua conta foi criada com uma senha temporária. Escolha uma nova senha para continuar.',
        });
        return;
      }
      finishLogin(result.tokens, values.email);
    } catch (err) {
      toast({ variant: 'danger', title: 'Falha no login', description: mapCognitoErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitNewPassword(values: NewPasswordInput) {
    if (!challenge) return;
    setLoading(true);
    try {
      const tokens = await challenge.complete(values.newPassword);
      finishLogin(tokens, challenge.email);
    } catch (err) {
      toast({ variant: 'danger', title: 'Falha ao definir senha', description: mapCognitoErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }

  if (challenge) {
    return (
      <MarketingLayout>
        <div className="container-px mx-auto max-w-md py-16">
          <Card>
            <CardHeader>
              <CardTitle>Defina sua senha</CardTitle>
              <CardDescription>
                Você está logando com a senha temporária enviada para <strong>{challenge.email}</strong>.
                Escolha uma senha definitiva para acessar a plataforma.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={newPasswordForm.handleSubmit(onSubmitNewPassword)} className="space-y-4">
                <div>
                  <Label htmlFor="new-password">Nova senha</Label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    autoFocus
                    className="mt-1.5"
                    {...newPasswordForm.register('newPassword')}
                  />
                  {newPasswordForm.formState.errors.newPassword && (
                    <p className="text-xs text-danger mt-1">
                      {newPasswordForm.formState.errors.newPassword.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Mínimo 8 caracteres, com maiúscula, minúscula e número.
                  </p>
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirme a nova senha</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    className="mt-1.5"
                    {...newPasswordForm.register('confirmPassword')}
                  />
                  {newPasswordForm.formState.errors.confirmPassword && (
                    <p className="text-xs text-danger mt-1">
                      {newPasswordForm.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>
                <Button type="submit" variant="accent" className="w-full" loading={loading}>
                  Definir senha e entrar
                </Button>
                <Button
                  type="button"
                  variant="link"
                  className="w-full"
                  onClick={() => {
                    setChallenge(null);
                    newPasswordForm.reset();
                  }}
                >
                  Voltar ao login
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <div className="container-px mx-auto max-w-md py-16">
        <Card>
          <CardHeader>
            <CardTitle>Entrar</CardTitle>
            <CardDescription>Acesse sua conta no Parecer.ai.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="mt-1.5"
                  {...form.register('email')}
                />
                {form.formState.errors.email && (
                  <p className="text-xs text-danger mt-1">{form.formState.errors.email.message}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Senha</Label>
                  <Link to="/esqueci-senha" className="text-xs text-muted-foreground hover:text-foreground">
                    Esqueci a senha
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className="mt-1.5"
                  {...form.register('password')}
                />
                {form.formState.errors.password && (
                  <p className="text-xs text-danger mt-1">{form.formState.errors.password.message}</p>
                )}
              </div>
              <Button type="submit" variant="accent" className="w-full" loading={loading}>
                Entrar
              </Button>
            </form>
            <p className="mt-6 text-sm text-center text-muted-foreground">
              Ainda não tem conta?{' '}
              <Link to="/cadastro" className="text-foreground font-medium hover:underline">
                Cadastre-se
              </Link>
            </p>
            <p className="mt-2 text-xs text-center text-muted-foreground">
              É advogado?{' '}
              <Link to="/cadastro/advogado" className="hover:text-foreground">
                Cadastro especial aqui
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </MarketingLayout>
  );
}
